/**
 * Target Selection — pick who the AI should shoot at.
 *
 * High-skill AI targets optimally (closest + highest damage% = easiest kill).
 * Low-skill AI picks randomly among alive opponents.
 *
 * Uses seeded RNG (not Math.random) — AI decisions affect game state.
 */

import { random } from '../terrain/rng';
import type { GameState } from '../types';
import type { SkillProfile } from './skill-profile';

/**
 * Select target player index for the AI to attack.
 * Returns -1 if no valid targets (shouldn't happen in normal play).
 */
export function selectTarget(
  state: GameState,
  aiPlayerIndex: number,
  profile: SkillProfile,
): number {
  const aiPlayer = state.players[aiPlayerIndex];
  const candidates: { index: number; score: number }[] = [];

  for (let i = 0; i < state.players.length; i++) {
    if (i === aiPlayerIndex) continue;
    const p = state.players[i];
    if (!p.alive) continue;

    // Score: higher = more attractive target
    // Prioritize high damage% (closer to kill) and proximity (easier to hit)
    const dx = p.x - aiPlayer.x;
    const dy = p.y - aiPlayer.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const distScore = 1 / (1 + dist / 500); // closer = higher
    const damageScore = p.damagePercent / 200; // more damage = more attractive

    candidates.push({ index: i, score: distScore + damageScore });
  }

  if (candidates.length === 0) return -1;

  // Decide: optimal pick vs random
  if (random() < profile.targetOptimalPct) {
    // Pick the best target by score
    candidates.sort((a, b) => b.score - a.score);
    return candidates[0].index;
  } else {
    // Random target
    return candidates[Math.floor(random() * candidates.length)].index;
  }
}
